"use client"

/**
 * VerdictExplorer — the interactive part of /verdict.
 *
 * No scan endpoint exists yet, so this is a truthful fallback: clicking
 * "Explore a verdict" reveals the real product UI (Answers · Brands ·
 * Sources) pre-rendered from DEMO_WORKSPACE, instantly — no staged loading,
 * no fake scan progress, no "live" label. The frame is always labelled
 * "Product view · Demo workspace".
 */

import Link from "next/link"
import { useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { MagneticBtn } from "@/components/landing/motion-primitives"
import { DEMO_WORKSPACE } from "@/lib/site-config"
import { fireAnalyticsEvent } from "@/lib/analytics"
import {
  BRAND_RANKS,
  CITED_SOURCES,
  SURFACE_ANSWERS,
  type PresenceStatus,
} from "./verdict-data"

const STATUS_STYLE: Record<PresenceStatus, { label: string; className: string }> = {
  PRIMARY: {
    label: "PRIMARY",
    className: "text-[var(--velova-blue-soft)] bg-[var(--velova-blue)]/[0.14] border-[var(--velova-blue)]/40",
  },
  SECONDARY: {
    label: "SECONDARY",
    className: "text-white/75 bg-white/[0.07] border-white/20",
  },
  MENTION: {
    label: "MENTION",
    className: "text-white/55 bg-white/[0.04] border-white/12",
  },
  ABSENT: {
    label: "ABSENT",
    className: "text-white/35 bg-transparent border-white/10",
  },
}

type TabId = "answers" | "brands" | "sources"
const TABS: { id: TabId; label: string }[] = [
  { id: "answers", label: "Answers" },
  { id: "brands", label: "Brands" },
  { id: "sources", label: "Sources" },
]

export function VerdictExplorer() {
  const [revealed, setRevealed] = useState(false)
  const [tab, setTab] = useState<TabId>("answers")
  const resultsRef = useRef<HTMLDivElement>(null)

  const handleExplore = () => {
    fireAnalyticsEvent("verdict_started")
    setRevealed(true)
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  const handleTrackClick = () => {
    fireAnalyticsEvent("verdict_track_clicked")
  }

  return (
    <div>
      {!revealed && (
        <div className="mt-9">
          <MagneticBtn>
            <button type="button" onClick={handleExplore} className="velova-btn velova-btn-ink">
              Explore a verdict
              <ArrowRight className="velova-btn-arrow w-[14px] h-[14px]" />
            </button>
          </MagneticBtn>
        </div>
      )}

      <AnimatePresence>
        {revealed && (
          <motion.div
            ref={resultsRef}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="mt-10 scroll-mt-24"
          >
            <div className="velova-block p-5 sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5">
                <span className="velova-mono-label text-white/60">{DEMO_WORKSPACE.frameLabel}</span>
              </div>

              <h2 className="velova-display mt-6 text-[26px] sm:text-[32px] leading-[1.1]">
                Your brand ranked {DEMO_WORKSPACE.position} across returned answers.
              </h2>
              <p className="mt-3 text-[15px] sm:text-[16px] text-[var(--velova-ink-soft)]">
                First choice in {DEMO_WORKSPACE.firstChoice} · {DEMO_WORKSPACE.competitorsAhead} competitors ahead ·{" "}
                {DEMO_WORKSPACE.citedSources} cited sources
              </p>

              {/* Tabs */}
              <div className="mt-8 flex items-center gap-1 border-b border-[var(--velova-line)]">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    aria-current={tab === t.id}
                    className={`relative px-4 py-2.5 text-[13.5px] font-medium transition-colors rounded-t-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--velova-blue)] ${
                      tab === t.id ? "text-[var(--velova-ink)]" : "text-white/45 hover:text-white/70"
                    }`}
                  >
                    {t.label}
                    {tab === t.id && (
                      <motion.span
                        layoutId="verdict-tab-underline"
                        className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--velova-blue)]"
                        transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
                      />
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                {tab === "answers" && <AnswersTab />}
                {tab === "brands" && <BrandsTab />}
                {tab === "sources" && <SourcesTab />}
              </div>

              {/* CTAs */}
              <div className="mt-9 pt-7 border-t border-[var(--velova-line)] flex flex-col sm:flex-row gap-3">
                <MagneticBtn>
                  <Link
                    href="/signup"
                    onClick={handleTrackClick}
                    className="velova-btn velova-btn-ink w-full sm:w-auto justify-center"
                  >
                    Track this every day
                    <ArrowRight className="velova-btn-arrow w-[14px] h-[14px]" />
                  </Link>
                </MagneticBtn>
                <MagneticBtn strength={8}>
                  <Link
                    href="/how-it-works"
                    className="velova-btn velova-btn-ghost w-full sm:w-auto justify-center"
                  >
                    See how Velova works
                  </Link>
                </MagneticBtn>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ─── Answers tab — one row per surface ─────────────────────────── */
function AnswersTab() {
  return (
    <div>
      <p className="velova-mono-label text-white/45 mb-1.5">Buyer question</p>
      <p className="text-[15px] sm:text-[16px] text-[var(--velova-ink)] mb-6">
        &ldquo;{DEMO_WORKSPACE.buyerQuestion}&rdquo;
      </p>
      <div className="flex flex-col gap-2">
        {SURFACE_ANSWERS.map((row) => {
          const st = STATUS_STYLE[row.status]
          return (
            <div
              key={row.surface}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-[var(--velova-line)] px-4 py-3.5"
            >
              <div className="flex items-center gap-3 sm:w-[220px] shrink-0">
                <span className="text-[13.5px] font-medium text-[var(--velova-ink)]">{row.surface}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-medium uppercase tracking-[0.12em] ${st.className}`}
                >
                  {st.label}
                </span>
                {row.firstChoice && (
                  <span className="text-[9.5px] font-medium uppercase tracking-[0.12em] text-[var(--velova-blue-soft)]">
                    First choice
                  </span>
                )}
              </div>
              <p className="text-[13px] text-white/50">
                {row.named.map((name, i) => (
                  <span key={name}>
                    <span className={name === DEMO_WORKSPACE.brand ? "text-[var(--velova-ink)]" : undefined}>
                      {name}
                    </span>
                    {i < row.named.length - 1 && <span className="text-white/25"> · </span>}
                  </span>
                ))}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ─── Brands tab — ranked shortlist ─────────────────────────────── */
function BrandsTab() {
  return (
    <div className="flex flex-col gap-2">
      {BRAND_RANKS.map((brand, i) => (
        <div
          key={brand.name}
          className={`flex items-center gap-4 rounded-lg border px-4 py-3.5 ${
            brand.isYou
              ? "border-[var(--velova-blue)]/40 bg-[var(--velova-blue)]/[0.08]"
              : "border-[var(--velova-line)]"
          }`}
        >
          <span className="w-5 shrink-0 text-[13px] tabular-nums text-white/40">{i + 1}</span>
          <span className="text-[14px] font-medium text-[var(--velova-ink)]">
            {brand.name}
            {brand.isYou && <span className="ml-2 text-[11px] font-normal text-[var(--velova-blue-soft)]">You</span>}
          </span>
          <span className="ml-auto text-[12.5px] text-white/50 tabular-nums">
            {brand.appearedIn} of {SURFACE_ANSWERS.length} answers
          </span>
          {brand.firstChoiceIn > 0 && (
            <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--velova-blue-soft)] hidden sm:inline">
              {brand.firstChoiceIn} first choice
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

/* ─── Sources tab — cited pages/domains ─────────────────────────── */
function SourcesTab() {
  return (
    <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1">
      {CITED_SOURCES.map((source) => (
        <div
          key={source.domain}
          className="flex items-start justify-between gap-4 rounded-lg border border-[var(--velova-line)] px-4 py-3.5"
        >
          <div>
            <p className="text-[13.5px] text-[var(--velova-ink)]">{source.title}</p>
            <p className="mt-1 text-[12px] text-white/40">{source.domain}</p>
          </div>
          <ArrowUpRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-white/25" />
        </div>
      ))}
    </div>
  )
}
